import { defineStore } from "pinia";
import { remindersService } from "@/api/services/esupplyvet/remindersService";
import { animalsService } from "@/api/services/esupplyvet/animalsService";

export const useRemindersStore = defineStore("reminders", {
  state: () => ({
    reminders: [],
    animals: [],
    animalTypes: [],
    loading: false,
    error: null,
  }),

  getters: {
    /**
     * Retourne la liste des rappels avec formatage pour l'affichage
     */
    formattedReminders: state => {
      return state.reminders.map(reminder => {
        const animal = state.animals.find(a => a.id === reminder.animalId);
        const animalType = animal ? state.animalTypes.find(type => type.id === animal.animalTypeId) : null;

        const animalName = animal ? animal.name : "Animal inconnu";
        const typeName = animalType ? animalType.name : null;

        return {
          ...reminder,
          displayName: animalName,
          formattedDate: reminder.reminderDate ? new Date(reminder.reminderDate).toLocaleDateString("fr-FR") : null,
          notificationMethods:
            reminder.notificationMethods && reminder.notificationMethods.length > 0
              ? Array.isArray(reminder.notificationMethods)
                ? reminder.notificationMethods.join(", ")
                : reminder.notificationMethods
              : "Non spécifié",
          title: reminder.title || "Sans titre",
          animalDisplayName: typeName ? ` ${animalName} (${typeName})` : `${animalName}`,
        };
      });
    },
    hasReminders: state => state.reminders.length > 0,
  },

  actions: {
    /**
     * Récupère la liste des rappels depuis l'API
     */
    async fetchReminders() {
      this.loading = true;
      this.error = null;

      try {
        const [remindersData, animalsData, animalTypesData] = await Promise.all([
          remindersService.getReminders(),
          animalsService.getAnimals(),
          animalsService.getAnimalTypes(),
        ]);

        this.reminders = remindersData;
        this.animals = animalsData;
        this.animalTypes = animalTypesData;
      } catch (error) {
        this.error = "Erreur lors du chargement des rappels";
        console.error("Erreur fetchReminders:", error);
      } finally {
        this.loading = false;
      }
    },

    /**
     * Récupère un rappel par son ID
     * @param {number|string} id - L'ID du rappel à récupérer
     */
    async getReminder(id) {
      try {
        const data = await remindersService.getReminder(id);
        return data;
      } catch (error) {
        this.error = "Erreur lors de la récupération du rappel";
        console.error("Erreur getReminder:", error);
        throw error;
      }
    },

    /**
     * Met à jour un rappel par son ID
     * @param {number|string} id - L'ID du rappel à mettre à jour
     * @param {Object} reminderData - Les données du rappel à mettre à jour
     */
    async updateReminder(id, reminderData) {
      try {
        const updatedReminderData = {
          ...reminderData,
          notificationMethods: Array.isArray(reminderData.notificationMethods)
            ? reminderData.notificationMethods
            : reminderData.notificationMethods
              ? [reminderData.notificationMethods]
              : [],
        };
        const updatedReminder = await remindersService.updateReminder(id, updatedReminderData);
        const index = this.reminders.findIndex(reminder => reminder.id === id);
        if (index !== -1) {
          this.reminders[index] = updatedReminder;
        }
        return updatedReminder;
      } catch (error) {
        this.error = "Erreur lors de la modification du rappel";
        console.error("Erreur updateReminder:", error);
        throw error;
      }
    },

    /**
     * Crée un nouveau rappel
     * @param {Object} reminderData - Les données du rappel à créer
     */
    async createReminder(reminderData) {
      try {
        const { useAuthStore } = await import("../authStore.js");
        const authStore = useAuthStore();
        const updatedReminderData = {
          ...reminderData,
          ownerId: authStore.user?.id || null,
          notificationMethods: Array.isArray(reminderData.notificationMethods)
            ? reminderData.notificationMethods
            : reminderData.notificationMethods
              ? [reminderData.notificationMethods]
              : [],
        };
        const newReminder = await remindersService.createReminder(updatedReminderData);
        this.reminders.push(newReminder);
        return newReminder;
      } catch (error) {
        this.error = "Erreur lors de la création du rappel";
        console.error("Erreur createReminder:", error);
        throw error;
      }
    },

    /**
     * Supprime un rappel par son ID
     * @param {number|string} id - L'ID du rappel à supprimer
     */
    async deleteReminder(id) {
      try {
        await remindersService.deleteReminder(id);
        this.reminders = this.reminders.filter(reminder => reminder.id !== id);
      } catch (error) {
        this.error = "Erreur lors de la suppression du rappel";
        console.error("Erreur deleteReminder:", error);
        throw error;
      }
    },

    /**
     * Remet à zéro le store
     */
    resetReminders() {
      this.reminders = [];
      this.animals = [];
      this.animalTypes = [];
      this.loading = false;
      this.error = null;
    },
  },
});
